import {Container, Graphics, Sprite, TextStyle, Text} from 'pixi.js';
import {
    Button, consoleLogger,
    CoreDI, FullScreenFader,
    IEventManagerWithAsync,
    inject, KeyboardModule,
    LanguageService,
    ModuleInitPhase,
    MovieClip, MovieClipTween, ObjectBuilder, ScreenParameters, Timer, Tween,
    UpdateManager
} from '@gongxigames/framework/core';
import {DI} from '@gongxigames/framework/slot/configs/DI';
import {LAYERS} from '@gongxigames/framework/slot/configs/Layers';
import {SlotSettings} from '@gongxigames/framework/slot/configs/SlotSettings';
import {GameConfig, ILogic} from '@gongxigames/framework/slot/types';
import {GlobalEventsMapForBackwardCompatibility} from '@gongxigames/framework/slot/events';
import {ILoadingScreenView} from '@gongxigames/framework/slot/modules/loadingScreen/ILoadingScreenView';
import {LoadingScreenModule} from '@gongxigames/framework/slot/modules/loadingScreen/LoadingScreenModule';
import {Progress} from '@gongxigames/framework/slot/modules/loadingScreen/components/Progress';
import {View} from '../../fragments/module/view';

export class LoadingScreenViewOld extends View implements ILoadingScreenView {
    public static NAME = LoadingScreenModule.VIEW;
    public static initPhase: ModuleInitPhase = 'initial';
    public static layer = LAYERS.TOP;

    protected static readonly SLIDER_FADE_DELAY: number = 500;
    protected static readonly MOBILE_PORTRAIT_Y: number = 680;
    protected static readonly DOT_OFFSET: number = 30;
    protected static readonly BETWEEN_SLIDES_DELAY: number = 800;
    protected static readonly PRELOADER_TEXT_ID: string = 'preloaderOfGame';
    protected static readonly PRELOADER_TEXT_UK_TAG: string = '_UK';
    protected static readonly TEXT_STYLES: TextStyle = new TextStyle({
        align: 'center',
        dropShadow: true,
        dropShadowAlpha: 20,
        dropShadowAngle: 90,
        dropShadowBlur: 10,
        dropShadowDistance: 4,
        fontFamily: 'PF DinDisplay Pro',
        fontSize: 75,
        fontWeight: 'bold',
        fill: [
            '#fcfc17',
            '#fefec5',
            '#ffee40',
            '#ff9e00',
            '#fd7a13'
        ],
        stroke: '#84230f',
        strokeThickness: 10,
        wordWrap: false,
        wordWrapWidth: 1800
    });

    @inject(CoreDI.LanguageService)
    protected _languageService!: LanguageService;

    @inject(CoreDI.GameConfig)
    protected _gameConfig!: GameConfig;

    @inject(DI.Logic)
    protected _logic!: ILogic;

    @inject(CoreDI.IEventManagerWithAsync)
    protected _eventManager!: IEventManagerWithAsync<GlobalEventsMapForBackwardCompatibility>;

    protected _container: Sprite = new Sprite();
    protected _loader?: Sprite;
    protected _startButton!: Button;
    protected _progress!: Progress;
    protected _nextButton!: Button;
    protected _previousButton!: Button;
    protected _currentSlide = 0;
    protected _slides!: MovieClip;
    protected _textSlides!: MovieClip;
    protected _maxTextHeight = 0;
    protected _dots!: Sprite;
    protected _fader = this.createFader();
    protected readonly _updater = new UpdateManager(true);
    protected _listener!: (keyCode: number) => void;
    protected shouldShowStartButton = true;

    public start(): void {
        this.initGraphicElements();
        this.resize();
        this.showProgressBar();
    }

    public updateProgress(progress: number): void {
        this._progress?.update(progress);
    }

    public loadingComplete(hideImmediately = false): void {
        this.updateProgress(1);

        if (hideImmediately) {
            this.hide();

            return;
        }

        this._updateManager.add(new Timer(() => {
            this.complete();
        }, 80, true));
    }

    public enableFirstInteraction(enable = true): void {
        this.shouldShowStartButton = enable;
        this.showStartButton();
    }

    public init(): void {
        super.init();
        this._updateManager.add(this._updater);
    }

    public onLandscape(): void {
        this._container.position.set(0);

        this.resize();
    }

    public onPortrait(): void {
        const x = (this._gameConfig.viewport.mobile.height - this._gameConfig.viewport.mobile.width) / 2;
        const y = LoadingScreenViewOld.MOBILE_PORTRAIT_Y;

        this._container.position.set(x, y);
        this.resize();
    }

    public onResized(screenParams: ScreenParameters): void {
        this.resize(screenParams);
    }

    protected checkSlideTexts(): void {
        this._slides.frames.forEach((frame, index) => {
            if (frame instanceof Sprite) {
                const texts = frame.getChildByName<Sprite>('texts');

                if (texts) {
                    texts.children.forEach(child => {
                        (child as Sprite).children.forEach(text => {
                            const textId = LoadingScreenViewOld.PRELOADER_TEXT_ID + (index + 1) + '_' + text.name;
                            const slideText = this._languageService.get(textId) || '';
                            const advancedText = this._objectBuilder
                                .buildAdvancedText({text: slideText, style: LoadingScreenViewOld.TEXT_STYLES});

                            (text as Sprite).addChild(advancedText);
                        });
                    });
                }
            }
        });
    }

    protected addTexts(): void {
        let isTextWithImage = false;

        this._textSlides.frames.forEach((frame) => {
            const slideSprite = (frame as Sprite).getChildByName<Container>('text');

            if (slideSprite) {
                const textId = this.getTextId(frame.name);
                const slideText = this._languageService.get(textId);

                if (slideText !== 'undefined') {
                    const text = this._objectBuilder.buildAdvancedText({text: slideText, style: LoadingScreenViewOld.TEXT_STYLES});
                    const textElementsContainer = text.children[0] as Container;

                    slideSprite.addChild(text);
                    this._maxTextHeight = Math.max(this._maxTextHeight, text.height);

                    if (textElementsContainer.children.some(child => !(child instanceof Text))) {
                        isTextWithImage = true;
                    }
                }
            }
        });

        if (!isTextWithImage) {
            this._maxTextHeight += LoadingScreenViewOld.DOT_OFFSET * 2;
        }
    }

    public hide(): void {
        super.hide();
        this.removeEvents();
        this.remove();
    }

    protected removeEvents(): void {
        super.removeEvents();

        if (this._eventManager.hasListener('General.UI.KEY_UP', this)) {
            this._eventManager.off('General.UI.KEY_UP', this);
        }
    }

    protected remove(): void {
        this._updateManager.remove(this._updater);
        this._updater.removeAll();
        this._container.parent?.removeChild(this._container);
    }

    private getTextId(frameName: string): string {
        let textId = LoadingScreenViewOld.PRELOADER_TEXT_ID + frameName;

        if (this.getSettings<SlotSettings>().get('ukRegulations')) {
            const ukTexId = textId + LoadingScreenViewOld.PRELOADER_TEXT_UK_TAG;
            const ukText = this._languageService.get(ukTexId);

            textId = ukText !== 'undefined' ? ukTexId : textId;
        }

        return textId;
    }

    private initGraphicElements(): void {
        const {game_sprite, shared_sprite} = ObjectBuilder.resources;
        const gameLoader = this._objectBuilder.buildSprite(game_sprite.loader);
        const loader = this._objectBuilder.buildSprite(shared_sprite.loader);

        this._loader = loader;

        this._startButton = loader.getChildByName<Button>('startButton');
        this._startButton.hitArea = this._startButton.getLocalBounds();
        this._startButton.visible = false;
        this._startButton.callback = this.onStartButtonClicked.bind(this);

        this._previousButton = loader.getChildByName<Button>('previousButton');
        this._previousButton.callback = this.previousSlide.bind(this);
        this._previousButton.visible = false;

        this._nextButton = loader.getChildByName<Button>('nextButton');
        this._nextButton.callback = this.nextSlide.bind(this);
        this._nextButton.visible = false;

        this._dots = loader.getChildByName<Sprite>('dots');
        this._slides = gameLoader.getChildByName<MovieClip>('slides');
        this._textSlides = gameLoader.getChildByName<MovieClip>('textSlides');

        this._progress = new Progress(this._objectBuilder, loader.getChildByName<Sprite>('bar'));
        this._loader.addChild(this._progress.container);

        this._parent.addChild(this._container);
        this._container.addChild(this._fader!);
        this._container.addChild(gameLoader);
        this._container.addChild(loader);

        this._container.visible = false;

        this.updateProgress(0);
    }

    private complete(): void {
        this.showStartButton();
        this._progress.container.visible = false;
    }

    public showStartButton(): void {
        if (this.shouldShowStartButton) {
            this.initButtons();
            this._startButton.visible = true;
        }
    }

    private showProgressBar(): void {
        this._container.visible = true;
        this._container.alpha = 1;

        if (this._slides) {
            this.initTexts();
            this.playSlides();
        }
    }

    private playSlides(): void {
        let shouldShowArrows = false;

        if (this._slides) {
            if (this._slides.totalFrames > 1) {
                this.initDots();
                this.nextSlide();
                shouldShowArrows = true;
            } else if (this._slides.totalFrames === 1) {
                if (this._currentSlide < 1) {
                    this._currentSlide = this._slides.frames.length;
                }
                this.updateSlide();
            } else {
                consoleLogger.warn('Please add "slides" MovieClip into "loader" sprite');
            }
        } else {
            consoleLogger.warn('Please add "slides" MovieClip into "loader" sprite');
        }

        this._previousButton.visible = shouldShowArrows;
        this._nextButton.visible = shouldShowArrows;
    }

    private initTexts(): void {
        if (this._slides) {
            this.checkSlideTexts();
        }

        if (this._textSlides) {
            this.addTexts();
            this.updateTextPositions();
        } else {
            this.calculateTextHeight();
        }

        this.updatePositions();
    }

    private updateTextPositions(): void {
        this._textSlides.frames.forEach(frame => {
            const slideSprite = (frame as Sprite).getChildByName<Container>('text');
            const text = slideSprite.children[0] as Text;

            if (text.height < this._maxTextHeight) {
                text.y += (this._maxTextHeight - text.height) / 2;
            }
        });
    }

    private calculateTextHeight(): void {
        this._slides.frames.forEach((slide) => {
            let textImage;

            if (slide instanceof MovieClip) {
                textImage = (slide.frames[0] as Sprite).children[1] as Sprite;
            }

            if (textImage) {
                this._maxTextHeight = Math.max(this._maxTextHeight, textImage.height);
            }
        });
    }

    private updatePositions(): void {
        this._startButton.y += this._maxTextHeight;
        this._progress.container.y += this._maxTextHeight;
        this._dots.y += this._maxTextHeight;
    }

    private initButtons(): void {
        const KEYS = [KeyboardModule.ENTER_CODE, KeyboardModule.SPACEBAR_CODE];

        this._listener = (keyCode: number) => {
            if (KEYS.includes(keyCode)) {
                this.onStartButtonClicked();
            }
        };

        this._eventManager.on('General.UI.KEY_UP', this._listener, this);
    }

    private initDots(): void {
        let x = 0;

        for (let i = 0; i < this._slides.totalFrames; i++) {
            const dot = this._objectBuilder.buildDisplayObjectByName('sprites', 'loader.dot') as Button;

            dot.callback = this.onDotClicked.bind(this, i);
            this._dots.addChild(dot);
            dot.x = x;
            x += dot.getLocalBounds().width + LoadingScreenViewOld.DOT_OFFSET;
        }

        this._dots.x = (this._gameConfig.viewport.desktop.width - this._dots.getLocalBounds().width) / 2;
    }

    private createFader(): Graphics {
        const fader = new FullScreenFader({name: 'fader'});

        fader.interactive = true;

        return fader;
    }

    private resize(screenParams?: ScreenParameters): void {
        screenParams ??= this._screenManager.getScreenState();

        const {scale, canvasWidth, canvasHeight, x, y} = screenParams;

        this._fader!.width = canvasWidth / scale;
        this._fader!.height = canvasHeight / scale;

        const offsetX = -(this._container.position.x + (x / scale));
        const offsetY = -(this._container.position.y + (y / scale));

        this._fader!.position.set(offsetX, offsetY);
    }

    private onStartButtonClicked(): void {
        this.hide();
        this.emit('LoadingScreen.START_BUTTON_PRESSED');
    }

    private previousSlide(): void {
        this._updater.removeAll();
        this._currentSlide--;

        if (this._currentSlide < 1) {
            this._currentSlide = this._slides.frames.length;
        }

        this.updateSlide();
    }

    private nextSlide(): void {
        this._updater.removeAll();
        this._currentSlide++;

        if (this._currentSlide > this._slides.frames.length) {
            this._currentSlide = 1;
        }

        this.updateSlide();
    }

    private updateSlide(): void {
        this._slides.alpha = 0;
        this._slides.gotoAndStop(this._currentSlide);
        this.updateTextSlide();

        const frame = this._slides.getCurrentFrameObject();

        this.fadeSlider(true, this._slides, () => {
            if (frame instanceof MovieClip) {
                const slideMC = frame as MovieClip;

                slideMC.gotoAndStop(1);
                this._updater.add(new MovieClipTween(slideMC, 6, false)
                    .onComplete(this.onSliderMcCompleted.bind(this)).start());
            } else {
                this._updater.add(new Timer(this.onSliderSpriteTimerCompleted.bind(this),
                    this.getSpriteFrameDelay(), true));
            }
        });

        this.updateDots();
    }

    private updateTextSlide(): void {
        if (this._textSlides) {
            this._textSlides.visible = false;

            this._textSlides.frames.forEach((frame, index) => {
                if (frame.name === this._currentSlide.toString()) {
                    this._textSlides.gotoAndStop(index + 1);
                    this._textSlides.visible = true;
                }
            });
        }
    }

    private onSliderMcCompleted(): void {
        if (this._slides.frames.length > 1) {
            this.moveToNextSlider(this._slides);
        }
    }

    private onSliderSpriteTimerCompleted(): void {
        if (this._slides.frames.length > 1) {
            this.moveToNextSlider(this._slides);
        }
    }

    private getSpriteFrameDelay(): number {
        const frame = this._slides.getCurrentFrameObject();

        if (frame instanceof Sprite) {
            const frameNum = Number(frame.name);

            if (frameNum && frameNum > 0) {
                return frameNum;
            }
        }

        return LoadingScreenViewOld.BETWEEN_SLIDES_DELAY;
    }

    private moveToNextSlider(slider: MovieClip): void {
        this._updater.add(new Timer(() => {
            this.fadeSlider(false, slider, this.nextSlide.bind(this));
        }, LoadingScreenViewOld.BETWEEN_SLIDES_DELAY, true));
    }

    private fadeSlider(isFadeIn: boolean, slider: MovieClip, callBack: Function): void {
        this._updater.add(new Tween(slider)
            .to({alpha: isFadeIn ? 1 : 0}, LoadingScreenViewOld.SLIDER_FADE_DELAY)
            .onComplete(() => {
                if (callBack) {
                    callBack();
                }
            })
            .start());
    }

    private updateDots(): void {
        if (this._dots) {
            (this._dots.children as Button[]).forEach((dot, index) => {
                if (this._currentSlide === index + 1) {
                    dot.disable();
                } else {
                    dot.enable();
                }
            });
        }
    }

    private onDotClicked(index: number): void {
        this._updater.removeAll();
        this._currentSlide = index + 1;
        this.updateSlide();
    }
}
